import type IUserData from '../interface/IUserData';
import { normalizeHierarchyLabel } from '../types/hierarchy';
import type { HierarchyLabel } from '../types/hierarchy';
import type { SessionIdentity } from '../types/auth';

export const USER_DATA_CACHE_KEY = 'userDataLocal';
export const HIERARCHY_CACHE_KEY = 'hierarchyLabel';

type CacheEnvelope<T> = {
  version: 1;
  data: T;
};

type UserDataCache = CacheEnvelope<IUserData> & SessionIdentity;
type HierarchyCache = CacheEnvelope<HierarchyLabel> & { empresaId: string };

const readJson = <T,>(key: string): T | null => {
  try {
    const serialized = localStorage.getItem(key);
    return serialized ? JSON.parse(serialized) as T : null;
  } catch {
    try {
      localStorage.removeItem(key);
    } catch {
      // Storage may be unavailable; treat the value as a cache miss.
    }
    return null;
  }
};

const isUserData = (value: unknown): value is IUserData => {
  if (!value || typeof value !== 'object') return false;
  const data = value as Partial<IUserData>;
  return typeof data.id === 'string'
    && typeof data.userName === 'string'
    && typeof data.role === 'string'
    && Array.isArray(data.listaFuncionalidades);
};

export const readUserDataCache = (identity: SessionIdentity): IUserData | null => {
  const cache = readJson<UserDataCache>(USER_DATA_CACHE_KEY);
  if (
    !cache
    || cache.version !== 1
    || cache.userId !== identity.userId
    || cache.empresaId !== identity.empresaId
    || !isUserData(cache.data)
  ) return null;

  return cache.data;
};

export const writeUserDataCache = (identity: SessionIdentity, data: IUserData) => {
  const cache: UserDataCache = { version: 1, ...identity, data };
  try {
    localStorage.setItem(USER_DATA_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Keep the in-memory Context usable when browser storage is unavailable.
  }
};

export const readHierarchyCache = (empresaId: string): HierarchyLabel | null => {
  const cache = readJson<HierarchyCache>(HIERARCHY_CACHE_KEY);
  if (
    !cache
    || cache.version !== 1
    || cache.empresaId !== empresaId
  ) return null;

  return normalizeHierarchyLabel(cache.data);
};

export const writeHierarchyCache = (empresaId: string, data: HierarchyLabel) => {
  const cache: HierarchyCache = { version: 1, empresaId, data };
  try {
    localStorage.setItem(HIERARCHY_CACHE_KEY, JSON.stringify(cache));
  } catch {
    // Keep the in-memory Context usable when browser storage is unavailable.
  }
};

export const clearPersistedUserSessionData = () => {
  try {
    [
      USER_DATA_CACHE_KEY,
      HIERARCHY_CACHE_KEY,
      'userData',
      'loginData',
      'userInfoAuxiliar',
      '@secure.s.token',
      '@secure.s.password',
    ].forEach((key) => localStorage.removeItem(key));
  } catch {
    // Session state still clears from memory when browser storage is unavailable.
  }
};
